// Running as a Discord Activity.
//
// An Activity is this same page in an iframe inside Discord, so almost nothing
// here is Discord-specific: the game boots the way it always does and this
// module answers two questions around it -- are we inside Discord, and if so
// who is playing.
//
// The whole handshake is optional by design. If Discord is unreachable, or the
// user declines the authorisation prompt, the match still starts; it just does
// not know anyone's name. A card game that refuses to run because an identity
// service is down is worse than an anonymous one.

import { DiscordSDK, RPCCloseCodes } from '@discord/embedded-app-sdk';

/**
 * The application's client id. Public by design -- it is in the URL of every
 * Activity and in Discord's own OAuth dialogs. The *secret* is the other half
 * and never leaves the server; see `server/token.mjs`.
 */
export const DISCORD_CLIENT_ID = '1544388344039870636';

export interface DiscordUser {
  id: string;
  username: string;
  globalName: string | null;
  avatarUrl: string | null;
}

/**
 * 5000. Discord's code for "the player dismissed the authorisation prompt".
 *
 * Worth separating from every other way the handshake can fail. A network
 * blip or a slow client is ours to survive quietly; this one is a decision the
 * player made, it cannot be undone from in here, and it leaves the Activity
 * unable to do the thing they opened it for.
 */
function wasCancelled(err: unknown): boolean {
  if (err && typeof err === 'object') {
    const e = err as { code?: unknown; message?: unknown };
    if (e.code === 5000) return true;
    if (typeof e.message === 'string' && /cancel/i.test(e.message)) return true;
  }
  return /cancel/i.test(describeError(err));
}

export interface DiscordContext {
  sdk: DiscordSDK;
  user: DiscordUser | null;
  /**
   * The Discord access token, which is how the server establishes who is
   * calling: it asks Discord rather than believing the browser. Null when the
   * handshake did not complete, and then nothing is saved.
   */
  token: string | null;
  /** Why identifying the player failed, when it did. Shown, not swallowed. */
  error?: string;
  /** The voice channel or DM this instance is running in, when Discord says. */
  channelId: string | null;
  guildId: string | null;
  /**
   * Everyone in this Activity instance, which is the natural lobby: Discord
   * has already decided who is in the room.
   */
  instanceId: string | null;
}

/**
 * Rejects if a promise has not settled in time.
 *
 * Used for every step of the handshake, because none of them are ours: a
 * cross-frame message that nobody answers is indistinguishable from one that
 * is merely slow, and the boot sequence cannot tell the difference either.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${what} timed out after ${ms}ms`)), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      // Rejected *as it came*. Wrapping it in `new Error(String(err))` was how
      // Discord's `{ code, message }` became "[object Object]" -- the one thing
      // that would have said what went wrong, destroyed on the way out.
      (err: unknown) => { clearTimeout(timer); reject(err); },
    );
  });
}

/**
 * Turns whatever a rejection carried into something a person can act on.
 *
 * Discord's SDK rejects with a plain object, not an Error: an RPC failure is
 * `{ code, message }`, and an OAuth failure often adds `error` and
 * `error_description`. `String()` renders all of that as "[object Object]".
 */
function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const parts: string[] = [];
    if (e.code !== undefined) parts.push(`code ${String(e.code)}`);
    for (const key of ['message', 'error', 'error_description']) {
      const value = e[key];
      if (typeof value === 'string' && value) parts.push(value);
    }
    if (parts.length > 0) return parts.join(': ');
    try {
      return JSON.stringify(err);
    } catch {
      return Object.prototype.toString.call(err);
    }
  }
  return String(err);
}

/**
 * Whether this page is being served inside a Discord Activity.
 *
 * Discord launches the iframe with `frame_id` on the query string, which is
 * the documented signal and does not require the SDK to have loaded. Checking
 * for an iframe alone would be wrong -- the game may legitimately be embedded
 * somewhere else -- and waiting for `ready()` to fail would block boot on a
 * timeout for every ordinary visitor.
 */
export function inDiscord(): boolean {
  const params = new URLSearchParams(globalThis.location?.search ?? '');
  return params.has('frame_id');
}

/**
 * Marks the page as running inside Discord, for the CSS to read.
 *
 * Discord draws its own controls over the activity -- a shelf on the right in
 * a chat launch, a collapse chevron in the corner -- and an embedded app
 * cannot hide them; they are the client's chrome, not the page's. What it can
 * do is not put anything underneath them, which is why this exists: the "End
 * turn" button was being clipped by the shelf.
 */
export function markDiscordChrome(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('in-discord', inDiscord());
}

/**
 * Completes the Activity handshake and identifies the player.
 *
 * The token exchange has to happen on the server because it needs the client
 * secret, which a browser must never hold.
 */
export async function connectDiscord(): Promise<DiscordContext | null> {
  if (!inDiscord()) return null;

  const sdk = new DiscordSDK(DISCORD_CLIENT_ID);
  // `ready()` waits for a handshake with the Discord client, and if there is no
  // Discord on the other side it does not fail -- it simply never settles. Boot
  // awaits this, so an unbounded wait is a game that never starts: for anyone
  // who lands on a URL with a stray `frame_id`, and for everyone if Discord has
  // a bad minute. The timeout is the difference between degrading and hanging.
  await withTimeout(sdk.ready(), 8000, 'Discord handshake');

  const context: DiscordContext = {
    sdk,
    user: null,
    token: null,
    channelId: sdk.channelId ?? null,
    guildId: sdk.guildId ?? null,
    instanceId: sdk.instanceId ?? null,
  };

  try {
    // No `prompt: 'none'`. That value means "proceed only if this user has
    // already consented", so on a first launch -- when there is nothing to
    // consent with yet -- Discord refuses instead of asking, and the Activity
    // silently falls back to Guest forever. Omitting it shows the consent
    // dialog once and never again.
    //
    // `identify` alone, because that is all this uses. Every extra scope is
    // another thing to be refused for.
    const { code } = await withTimeout(sdk.commands.authorize({
      client_id: DISCORD_CLIENT_ID,
      response_type: 'code',
      state: '',
      scope: ['identify'],
    }), 60_000, 'Discord authorisation');

    const response = await fetch('/api/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) throw new Error(`token exchange failed: ${response.status}`);
    const { access_token: accessToken } = await response.json() as { access_token?: string };
    if (!accessToken) throw new Error('token exchange returned no access token');

    context.token = accessToken;
    const auth = await sdk.commands.authenticate({ access_token: accessToken });
    if (auth?.user) {
      context.user = {
        id: auth.user.id,
        username: auth.user.username,
        globalName: (auth.user as { global_name?: string | null }).global_name ?? null,
        avatarUrl: auth.user.avatar
          ? `https://cdn.discordapp.com/avatars/${auth.user.id}/${auth.user.avatar}.png?size=128`
          : null,
      };
    }
  } catch (err) {
    // Identity is a nicety; the match is the point. But a silent fallback to
    // "Guest" is indistinguishable from not being signed in on purpose, and
    // this failed once for a reason nothing on screen could show -- so the
    // reason is kept and the lobby prints it.
    context.error = describeError(err);

    // ...except when the player declined, which is not a degradation to play
    // through. Without a token there is no lobby, so they cannot be seated at
    // a table, cannot be seen by anyone else in the call, and nothing they do
    // in here will change that -- the prompt only comes back on a fresh
    // launch. So the Activity says why and closes itself, which is the one
    // action that leads anywhere.
    if (wasCancelled(err)) {
      try {
        sdk.close(RPCCloseCodes.CLOSE_NORMAL,
          'Riftbound needs you to authorise it to know who is playing.'
          + ' Launch the Activity again and choose Authorize.');
      } catch (closeErr) {
        // A close that will not close is not worth taking the match down for:
        // the lobby already explains the same thing in the panel.
        console.warn('could not close the Activity:', closeErr);
      }
    }
    // The raw value as well as the summary: the summary goes on screen, and
    // this is what makes the console worth opening when it is not enough.
    console.warn('Discord authentication failed:', err);
    // And reported, because inside the Discord desktop app there is no console
    // to open: a failure nobody can read is a failure nobody can fix.
    void fetch('/api/clientlog', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        at: 'discord-auth',
        summary: context.error,
        // Own properties only; an Error's message is not enumerable.
        detail: err instanceof Error ? { name: err.name, message: err.message }
          : err,
        instance: context.instanceId,
      }),
    }).catch(() => {});
  }

  return context;
}

/** The name to show for a player, falling back through what Discord gives us. */
export function displayName(user: DiscordUser | null): string {
  return user?.globalName ?? user?.username ?? 'You';
}
